import { generateClient } from 'aws-amplify/api';
import { v4 as uuid } from 'uuid';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import {
  boardWithMealRemoved,
  menuInputForShoppingListWithValidationErrors,
} from '../services/smorg_board';
import { shoppingListMealFromMeal } from '../features/end_user_shopping/services/shopping_lists';
import { deduplicate } from '../services/arrays';
import { EntryType, StructuredIngredientSource } from '../API';
import { mealsRemovedFromMealBasket } from '../features/end_user_shopping/end_user_shopping_slice';
import {
  ingredientsUpdatedWithAnalysisResult,
  mealWithIngredientTokensUpdated,
  mealWithScaledIngredientsReplaced,
} from '../services/meals';
import { showGlobalSnackbar } from '../features/global_snackbar/global_snackbar_slice';
import {
  mealBackgroundProcessing,
  mealFinishedBackgroundProcessing,
} from '../features/recipes/recipes_slice';
import { arraySectionFullTextIsHeading } from '../services/ingredients';

const client = generateClient();

export const parseRecipeByUrl = async (url) => {
  const recipesApiResponse = await client.graphql({
    query: mutations.addRecipe,
    variables: { url },
  });
  return recipesApiResponse.data.addRecipe;
};

// export const parseRecipesBulk = async (ldjsonListStr) => {
//   const recipesApiResponse = await client.graphql({
//     query: mutations.addRecipesBulk,
//     variables: { ldjsonListStr },
//   });
//   return recipesApiResponse.data.addRecipesBulk;
// };

export const analyzeIngredients = async (ingsFullText, customerFoodDataID) => {
  const recipesApiResponse = await client.graphql({
    query: mutations.analyzeIngredients,
    variables: {
      ingsFullText,
      customerFoodDataID,
    },
  });
  return recipesApiResponse.data.analyzeIngredients;
};

export const analyzeMissingIngredientsOperation = async (
  ingredients,
  customerFoodDataID,
) => {
  const nonBlankIngredients = ingredients.filter((ing) => !!ing.fullText);
  const ingredientsWithoutTokens = nonBlankIngredients.filter(
    (ing) =>
      !arraySectionFullTextIsHeading(ing.fullText) &&
      (!ing.tokens || !ing.tokFullText || !ing.structuredIngredient),
  );
  console.log({ ingredientsWithoutTokens });
  const ingsFullText = ingredientsWithoutTokens
    .map((ing) => ing.fullText)
    .filter((ingFullText) => !!ingFullText && ingFullText.length > 0);
  if (ingsFullText.length === 0) {
    return { ingsFullText, analyzedIngredients: [] };
  }
  return {
    ingsFullText,
    analyzedIngredients: await analyzeIngredients(
      ingsFullText,
      customerFoodDataID,
    ),
  };
};

export const deriveNutritionFromIngredientsOperation = async (
  ingredients,
  servings,
  publisher,
  chef,
  customerFoodDataID,
  derivedIngredientNutrition,
) => {
  const allStructuredIngInputs = ingredients
    .filter((ing) => !arraySectionFullTextIsHeading(ing.fullText))
    .map((ing, index) => {
      if (!ing.structuredIngredient) {
        return null;
      }
      const input = {
        ingredientID: ing.id,
        name: ing.structuredIngredient.name,
        quantity: ing.structuredIngredient.quantity,
        unitOfMeasure: ing.structuredIngredient.unitOfMeasure,
        linkedIngredientStatus:
          !!ing.structuredIngredient.linkedIngredient?.status,
        linkedIngredientName:
          ing.structuredIngredient.linkedIngredient?.linkedIngredientName,
      };
      if (
        ing.structuredIngredientSource === StructuredIngredientSource.MANUAL &&
        derivedIngredientNutrition
      ) {
        input.nutrition = derivedIngredientNutrition[index]?.nutrition || {};
        input.resolvedIngredientWeight =
          derivedIngredientNutrition[index]?.resolvedIngredientWeight;
      }
      return input;
    });
  const presentStructuredIngInputs = allStructuredIngInputs.filter(
    (strIng) => !!strIng,
  );
  return deriveNutritionOperation(
    servings,
    presentStructuredIngInputs,
    publisher,
    chef,
    customerFoodDataID,
  );
};

const deriveNutritionOperation = async (
  servings,
  structuredIngredients,
  publisher,
  chef,
  customerFoodDataID,
) => {
  const recipesApiResponse = await client.graphql({
    query: mutations.deriveNutrition,
    variables: {
      servings: servings || 1,
      structuredIngredients,
      publisher,
      chef,
      customerFoodDataID,
    },
  });
  return recipesApiResponse.data.deriveNutrition;
};

// export const deriveNutritionFromMultipleMealsIngredientsOperation = async (
//   meals,
// ) => {
//   const mealsRequest = meals.map((meal) => {
//     const recipe = meal.recipes[0];
//     const allStructuredIngs = recipe.ingredients.map(
//       (ing) =>
//         ing.structuredIngredient && {
//           ingredientID: ing.id,
//           name: ing.structuredIngredient.name,
//           quantity: ing.structuredIngredient.quantity,
//           unitOfMeasure: ing.structuredIngredient.unitOfMeasure,
//           linkedIngredientStatus:
//             !!ing.structuredIngredient.linkedIngredient?.status,
//           linkedIngredientName:
//             ing.structuredIngredient.linkedIngredient?.linkedIngredientName,
//         },
//     );
//     const presentStructuredIngs = allStructuredIngs.filter(
//       (strIng) => !!strIng,
//     );
//     return {
//       mealID: meal.id,
//       structuredIngredients: presentStructuredIngs,
//       servings: recipe.servings || 1,
//       publisher: recipe.publisher,
//       chef: recipe.chef,
//     };
//   });
//   return deriveNutritionMultipleMealsOperation(mealsRequest);
// };

// const deriveNutritionMultipleMealsOperation = async (mealsRequest) => {
//   const recipesApiResponse = await client.graphql({
//     query: mutations.deriveNutritionMeals, variables: {
//       meals: mealsRequest,
//       customerFoodDataID,
//     }),
//   );
//   return recipesApiResponse.data.deriveNutritionMeals;
// };

export const scaleIngredientsApiCall = async (
  scaleFactor,
  ingredients,
  customerFoodDataID,
) => {
  const recipesApiResponse = await client.graphql({
    query: mutations.scaleIngredients,
    variables: { scaleFactor, ingredients, customerFoodDataID },
  });
  return recipesApiResponse.data.scaleIngredients;
};

export const localiseIngredientsOperation = async (
  ingredients,
  localiseWhat,
  targetLocale,
) => {
  const recipesApiResponse = await client.graphql({
    query: mutations.localiseIngredients,
    variables: {
      ingredients,
      localiseWhat,
      targetLocale,
    },
  });
  return recipesApiResponse.data.localiseIngredients;
};

export const scaleMealIngredientsOperation = async (
  meal,
  scaleFactor,
  customerFoodDataID,
) => {
  const originalIngredients = meal.recipes[0].ingredients
    .filter((ing) => !!ing.fullText)
    .map((ing) => ({
      fullText: ing.fullText,
      tokFullText: ing.tokFullText,
      tokens: ing.tokens?.map((t) => ({
        fromChar: t.fromChar,
        toChar: t.toChar,
        label: t.label,
      })),
      structuredIngredient: {
        name: ing.structuredIngredient?.name,
        quantity: ing.structuredIngredient?.quantity,
        unitOfMeasure: ing.structuredIngredient?.unitOfMeasure,
        linkedIngredient: ing.structuredIngredient?.linkedIngredient,
      },
      scalingRules: ing.scalingRules || 'Linear',
    }));
  return scaleIngredientsApiCall(
    scaleFactor,
    originalIngredients,
    customerFoodDataID,
  );
};

export const compileShoppingListOperation = async (
  meals,
  servings,
  previousShoppingListItems,
  locale,
) => {
  const { menuInput } = menuInputForShoppingListWithValidationErrors(
    meals,
    servings,
  );

  const variables = { menu: menuInput, locale };

  if (previousShoppingListItems) {
    variables.previousIngredientCatalog = previousShoppingListItems.map(
      (item) => ({
        name: item.name,
        total: item.total,
        quantities: item.quantities,
        aisleLocation: item.aisleLocation,
        forMealIds: item.forMealIds,
      }),
    );
  }

  const compileIngredientCatalogResponse = await client.graphql({
    query: mutations.compileIngredientCatalog,
    variables,
  });
  console.log({ compileIngredientCatalogResponse });
  const newShoppingListItems =
    compileIngredientCatalogResponse.data.compileIngredientCatalog;

  const items = newShoppingListItems.map((item) => ({
    ...item,
    id: uuid(),
  }));

  const mealsForShoppingList = meals.map(shoppingListMealFromMeal);

  return {
    items,
    meals: mealsForShoppingList,
  };
};

// const migrateMeal = async (meal, currentSpaceMembershipID) => {
//   if (currentSpaceMembershipID && !meal.spaceMembershipID) {
//     return { ...meal, spaceMembershipID: currentSpaceMembershipID };
//   }
//   return null;
// };

export const getSuggestionsFromHistoryOperation = async (
  ingredientID,
  // ingredientFullText,
) => {
  const promises = [
    getSuggestionsFromHistoryBasedOnIngredientIDOperation(ingredientID),
    // getSuggestionsFromHistoryBasedOnFullTextOperation(ingredientFullText),
  ];
  const results = await Promise.all(promises);
  return deduplicate(results.flat());
};

const getSuggestionsFromHistoryBasedOnIngredientIDOperation = async (
  ingredientID,
) => {
  const suggestions = [];
  const fetchPage = async (nextToken) => {
    const suggestionsResponse = await client.graphql({
      query: queries.ingredientEditSuggestionByIngredientID,
      variables: {
        ingredientID,
        sortDirection: 'DESC',
        nextToken,
      },
    });
    suggestions.push(
      ...suggestionsResponse.data.ingredientEditSuggestionByIngredientID.items,
    );
    return suggestionsResponse.data.ingredientEditSuggestionByIngredientID
      .nextToken;
  };

  let nextToken = null;
  do {
    // eslint-disable-next-line no-await-in-loop
    nextToken = await fetchPage(nextToken);
  } while (nextToken !== null);

  return suggestions;
};

// const getSuggestionsFromHistoryBasedOnFullTextOperation = async (
//   ingredientFullText,
// ) => {
//   const suggestions = [];
//   const fetchPage = async (nextToken) => {
//     const suggestionsResponse = await client.graphql({
//       query: queries.ingredientEditSuggestionByInputFullText,
//       variables: {
//         inputFullText: ingredientFullText,
//         sortDirection: 'DESC',
//         nextToken,
//       },
//     });
//     suggestions.push(
//       ...suggestionsResponse.data.ingredientEditSuggestionByInputFullText.items,
//     );
//     return suggestionsResponse.data.ingredientEditSuggestionByInputFullText
//       .nextToken;
//   };

//   let nextToken = null;
//   do {
//     // eslint-disable-next-line no-await-in-loop
//     nextToken = await fetchPage(nextToken);
//   } while (nextToken !== null);

//   return suggestions;
// };

/**
 * Suggestions from food brain
 */
export const getSuggestionsOperation = async (
  ingredients,
  locale,
  customerFoodDataID,
) => {
  const searches = ingredients.map((ing) => ({
    fullText: ing.fullText,
    tokFullText: ing.tokFullText,
    tokens: ing.tokens.map((t) => ({
      fromChar: t.fromChar,
      toChar: t.toChar,
      label: t.label,
    })),
    structuredIngredient: {
      name: ing.structuredIngredient.name,
      quantity: ing.structuredIngredient.quantity,
      unitOfMeasure: ing.structuredIngredient.unitOfMeasure,
      linkedIngredient: ing.structuredIngredient.linkedIngredient,
    },
  }));
  const recipesApiResponse = await client.graphql({
    query: mutations.getSuggestions,
    variables: {
      searches,
      locale,
      customerFoodDataID,
    },
  });
  return recipesApiResponse.data.getSuggestions;
};

export const generateRecipeAIOperation = async (prompt) => {
  const generateResponse = await client.graphql({
    query: mutations.generateRecipeAI,
    variables: {
      prompt,
    },
  });
  return generateResponse.data.generateRecipeAI;
};

const storeObjectEditEventOperation = async (
  objectID,
  objectType,
  section,
  itemID,
  oldValue,
  currentHealthProGroup,
) => {
  const event = {
    objectID,
    objectType,
    section,
    itemID,
    oldValue,
    groups: currentHealthProGroup && [currentHealthProGroup],
  };
  const createResponse = await client.graphql({
    query: mutations.createObjectEditEvent,
    variables: { input: event },
  });
  return createResponse.data.createObjectEditEvent;
};

export const storeMealEditEventOperation = async (
  mealID,
  section,
  itemID,
  oldValue,
  currentHealthProGroup,
) =>
  storeObjectEditEventOperation(
    mealID,
    EntryType.MEAL,
    section,
    itemID,
    oldValue,
    currentHealthProGroup,
  );

export const getEditEventsForUndoIngredientEditOperation = async (
  mealID,
  ingredientID,
) => {
  const editEvents = [];
  const fetchPage = async (nextToken) => {
    const queryResponse = await client.graphql({
      query: queries.objectEditEventByObjectID,
      variables: {
        objectID: mealID,
        itemIDSectionCreatedAt: {
          beginsWith: `${ingredientID}#ingredients#`,
        },
        sortDirection: 'DESC',
        nextToken,
      },
    });
    editEvents.push(...queryResponse.data.objectEditEventByObjectID.items);
    return queryResponse.data.objectEditEventByObjectID.nextToken;
  };

  let nextToken = null;
  do {
    // eslint-disable-next-line no-await-in-loop
    nextToken = await fetchPage(nextToken);
  } while (nextToken !== null);

  return editEvents;
};

export const beginGenerateRecipeImageAIOperation = async (prompt, size) => {
  const response = await client.graphql({
    query: mutations.beginGenerateRecipeImageAI,
    variables: { prompt, size },
  });
  return response.data.beginGenerateRecipeImageAI;
};

export const checkGenerateRecipeImageAIOperation = async (jobID) => {
  const response = await client.graphql({
    query: mutations.checkGenerateRecipeImageAI,
    variables: { jobID },
  });
  return response.data.checkGenerateRecipeImageAI;
};

export const beginGenerateRecipeAIOperation = async (prompt) => {
  const response = await client.graphql({
    query: mutations.beginGenerateRecipeAI,
    variables: { prompt },
  });
  return response.data.beginGenerateRecipeAI;
};

export const checkGenerateRecipeAIOperation = async (jobID) => {
  const response = await client.graphql({
    query: mutations.checkGenerateRecipeAI,
    variables: { jobID },
  });
  return response.data.checkGenerateRecipeAI;
};

// export const beginModerateRecipeAIOperation = async (
//   ingredients,
//   preparations,
// ) => {
//   const response = await client.graphql({
//     query: mutations.beginModerateRecipeAI,
//     variables: {
//       ingredients,
//       preparations,
//     },
//   });
//   return response.data.beginModerateRecipeAI;
// };

export const checkModerateRecipeAIOperation = async (jobID) => {
  const response = await client.graphql({
    query: mutations.checkModerateRecipeAI,
    variables: { jobID },
  });
  return response.data.checkModerateRecipeAI;
};

export const beginLogMealAIOperation = async (prompt, customerFoodDataID) => {
  const response = await client.graphql({
    query: mutations.beginLogMealAI,
    variables: { prompt, customerFoodDataID },
  });
  return response.data.beginLogMealAI;
};

export const checkLogMealAIOperation = async (jobID) => {
  const response = await client.graphql({
    query: mutations.checkLogMealAI,
    variables: { jobID },
  });
  return response.data.checkLogMealAI;
};

export const updateFoodBrainDerivedDataOnMeal = async (
  mealID,
  recipe,
  derivedIngredientNutrition,
  customerFoodDataID,
  lazyGetMeal,
  updateMeal,
) => {
  if (!recipe) {
    return;
  }
  const { ingsFullText, analyzedIngredients } =
    await analyzeMissingIngredientsOperation(
      recipe.ingredients,
      customerFoodDataID,
    );
  let deriveNutritionIngsInput = recipe.ingredients;
  if (analyzedIngredients.length > 0) {
    for (let i = 0; i < ingsFullText.length; i += 1) {
      if (analyzedIngredients[i]) {
        analyzedIngredients[i].fullText = ingsFullText[i];
      }
    }
    deriveNutritionIngsInput = ingredientsUpdatedWithAnalysisResult(
      recipe.ingredients,
      analyzedIngredients,
    );
  }
  let deriveNutritionResult = null;
  if (recipe.servings) {
    try {
      deriveNutritionResult = await deriveNutritionFromIngredientsOperation(
        deriveNutritionIngsInput,
        recipe.servings,
        recipe.publisher,
        recipe.chef,
        customerFoodDataID,
        derivedIngredientNutrition,
      );
      // reportNutritionErrors(meal, deriveNutritionResult);
    } catch (e) {
      console.log(e);
    }
  }
  let { data: updatedMeal } = await lazyGetMeal(mealID, true);
  if (!updatedMeal) {
    console.warn(`Meal with ID ${mealID} not found for update.`);
    return;
  }
  if (analyzedIngredients.length > 0) {
    updatedMeal = mealWithIngredientTokensUpdated(
      updatedMeal,
      analyzedIngredients,
    );
  }
  if (deriveNutritionResult) {
    updatedMeal = {
      ...updatedMeal,
      derivedNutrition: deriveNutritionResult,
    };
  }
  await updateMeal(updatedMeal);
};

export const scaleMeal = async (
  meal,
  scaleFactor,
  scaleToServings,
  customerFoodDataID,
  skipUpdateDerivedData,
  updateMeal,
  dispatch,
  lazyGetMeal,
) => {
  dispatch(mealBackgroundProcessing([meal.id]));
  try {
    const scaledIngredients = await scaleMealIngredientsOperation(
      meal,
      scaleFactor,
      customerFoodDataID,
    );
    const updatedMeal = mealWithScaledIngredientsReplaced(
      meal,
      scaleToServings,
      scaledIngredients,
      scaleToServings / meal.recipes[0].servings,
    );
    await updateMeal(updatedMeal);
    if (!skipUpdateDerivedData) {
      await updateFoodBrainDerivedDataOnMeal(
        updatedMeal.id,
        updatedMeal.recipes[0],
        updatedMeal.derivedNutrition?.ingredientNutrition,
        customerFoodDataID,
        lazyGetMeal,
        updateMeal,
      );
    }
  } finally {
    dispatch(mealFinishedBackgroundProcessing(meal.id));
  }
};
export const storeChangedIngredientLinesOperation = (
  mealID,
  oldIngredients,
  newIngredients,
  currentHealthProGroup,
) => {
  const changedOldIngredientIndexes = oldIngredients
    .map((oldIng, i) =>
      oldIng.fullText !== newIngredients[i]?.fullText ? i : null,
    )
    .filter((idx) => idx !== null);
  console.log(JSON.stringify(changedOldIngredientIndexes));
  const promises = changedOldIngredientIndexes.flatMap((idx) => {
    const ingredientID = oldIngredients[idx].id;
    return [
      storeMealEditEventOperation(
        mealID,
        'ingredients',
        ingredientID,
        oldIngredients[idx].fullText,
        currentHealthProGroup,
      ),
    ];
  });
  return Promise.all(promises);
};

export const unfavouriteMeal = async (
  mealIDInFavourites,
  favouritesRecipesBoard,
  favouritesMenu,
  deleteMeal,
  updateSmorgBoard,
  dispatch,
) => {
  if (mealIDInFavourites) {
    console.log(`Begin unfavouriteMeal for ${mealIDInFavourites}`);
    dispatch(mealsRemovedFromMealBasket([mealIDInFavourites]));
    const updatedFavBoard = boardWithMealRemoved(
      favouritesRecipesBoard,
      mealIDInFavourites,
      favouritesMenu.id,
    );
    await Promise.all([
      deleteMeal({
        id: mealIDInFavourites,
        parentID: favouritesRecipesBoard.id,
      }),
      updateSmorgBoard(updatedFavBoard),
    ]);
    dispatch(
      showGlobalSnackbar({
        notificationText: 'Your meal has been removed from your favourites',
      }),
    );
    console.log(`End unfavouriteMeal for ${mealIDInFavourites}`);
  } else {
    console.warn(
      `unfavouriteMeal called with mealIDInFavourites = ${mealIDInFavourites}`,
    );
  }
};
